import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PortBerthingTypes } from '../enums/port-berthing-types.enum';
import { PortTariffLineUnitTypes } from '../enums/port-tariff-line-unit-types.enum';
import { PortTariffLineVesselProperties } from '../enums/port-tariff-line-vessel-properties.enum';
import { PortTariffLineExpenseCategories } from '../enums/port-tariff-line-expense-category.enum';
import { PortTariffLineOperationDirections } from '../enums/port-tariff-line-operation-direction.enum';
import { PortTariffLineModel } from '../models/port-tariff-line.model';
import { NumberUtils } from '../utils/number.utils';

type LinePreviewSource = Pick<PortTariffLineModel, 'lineType' | 'unitType' | 'vesselProperty' | 'rateValue' | 'quantityMultiplier'>;

@Injectable({
  providedIn: 'root',
})
export class PortTariffDisplayService {
  private readonly translateService = inject(TranslateService);

  berthingTypeLabel(value?: string | null): string {
    switch (value) {
      case PortBerthingTypes.ALONG_SIDE:
        return this.translate('portBerthingTypes.along_side');
      case PortBerthingTypes.MED_MOOR:
        return this.translate('portBerthingTypes.med_moor');
      case PortBerthingTypes.BOTH:
        return this.translate('portBerthingTypes.both');
      case PortBerthingTypes.ANCHOR:
        return this.translate('portBerthingTypes.anchor');
      case PortBerthingTypes.STERN_ONLY:
        return this.translate('portBerthingTypes.stern_only');
      case PortBerthingTypes.SIDE_POSSIBLE:
        return this.translate('portBerthingTypes.side_possible');
      case PortBerthingTypes.THIRD:
        return this.translate('portBerthingTypes.third');
      default:
        return value ?? '-';
    }
  }

  lineTypeLabel(value?: string | null): string {
    return value === 'tax'
      ? this.translate('portTariffLines.options.lineType.tax')
      : this.translate('portTariffLines.options.lineType.standard');
  }

  unitTypeLabel(value?: string | null): string {
    switch (value) {
      case PortTariffLineUnitTypes.FIXED:
        return this.translate('portTariffLines.options.unitType.fixed');
      case PortTariffLineUnitTypes.DAY:
        return this.translate('portTariffLines.options.unitType.day');
      case PortTariffLineUnitTypes.HOUR:
        return this.translate('portTariffLines.options.unitType.hour');
      case PortTariffLineUnitTypes.METER:
        return this.translate('portTariffLines.options.unitType.meter');
      case PortTariffLineUnitTypes.TON:
        return this.translate('portTariffLines.options.unitType.ton');
      case PortTariffLineUnitTypes.CARGO:
        return this.translate('portTariffLines.options.unitType.cargo');
      case PortTariffLineUnitTypes.CARGO_CBM:
        return this.translate('portTariffLines.options.unitType.cargoCbm');
      case PortTariffLineUnitTypes.CARGO_MT:
        return this.translate('portTariffLines.options.unitType.cargoMt');
      case PortTariffLineUnitTypes.CARGO_WM:
        return this.translate('portTariffLines.options.unitType.cargoWm');
      case PortTariffLineUnitTypes.PCT:
        return this.translate('portTariffLines.options.unitType.pct');
      default:
        return value ?? '-';
    }
  }

  vesselPropertyLabel(value?: string | null): string {
    switch (value) {
      case PortTariffLineVesselProperties.NONE:
        return this.translate('portTariffLines.options.vesselProperty.none');
      case PortTariffLineVesselProperties.LOA:
        return this.translate('portTariffLines.options.vesselProperty.loa');
      case PortTariffLineVesselProperties.BEAM:
        return this.translate('portTariffLines.options.vesselProperty.beam');
      case PortTariffLineVesselProperties.GRT:
        return this.translate('portTariffLines.options.vesselProperty.grt');
      case PortTariffLineVesselProperties.NRT:
        return this.translate('portTariffLines.options.vesselProperty.nrt');
      case PortTariffLineVesselProperties.DWT:
        return this.translate('portTariffLines.options.vesselProperty.dwt');
      default:
        return value ?? '-';
    }
  }

  expenseCategoryLabel(value?: string | null): string {
    switch (value) {
      case PortTariffLineExpenseCategories.PORT:
        return this.translate('portTariffLines.options.expenseCategory.port');
      case PortTariffLineExpenseCategories.STEVEDORING:
        return this.translate('portTariffLines.options.expenseCategory.stevedoring');
      case PortTariffLineExpenseCategories.AGENCY:
        return this.translate('portTariffLines.options.expenseCategory.agency');
      default:
        return value ?? '-';
    }
  }

  operationDirectionLabel(value?: string | null): string {
    switch (value) {
      case PortTariffLineOperationDirections.LOADING:
        return this.translate('portTariffLines.options.operationDirection.loading');
      case PortTariffLineOperationDirections.DISCHARGE:
        return this.translate('portTariffLines.options.operationDirection.discharge');
      case PortTariffLineOperationDirections.BOTH:
        return this.translate('portTariffLines.options.operationDirection.both');
      default:
        return value ?? '-';
    }
  }

  lineDisplayRate(line: Pick<PortTariffLineModel, 'lineType' | 'unitType' | 'rateValue'>): string {
    if (line.lineType === 'tax' && line.unitType === PortTariffLineUnitTypes.PCT) {
      return `${NumberUtils.formatRate(Number(line.rateValue ?? 0) * 100, 2)}%`;
    }

    return NumberUtils.formatMoney(line.rateValue, 4);
  }

  linePreview(line: LinePreviewSource): string {
    const rate = Number(line.rateValue ?? 0);
    const unitType = line.unitType;
    const vesselProperty = line.vesselProperty;
    const hasProperty = !!vesselProperty && vesselProperty !== PortTariffLineVesselProperties.NONE;

    if (line.lineType === 'tax') {
      if (unitType === PortTariffLineUnitTypes.PCT) {
        return `${NumberUtils.formatRate(rate * 100, 2)}% × ${this.translate('portTariffLines.preview.sumOfPrevious')}`;
      }
      return `${this.translate('portTariffLines.preview.fixed')} ${NumberUtils.formatMoney(rate, 2)}`;
    }

    const rateStr = NumberUtils.formatMoney(rate, 2);
    const parts: string[] = [rateStr];

    switch (unitType) {
      case PortTariffLineUnitTypes.DAY:
        parts.push(this.translate('portTariffLines.preview.days'));
        break;
      case PortTariffLineUnitTypes.HOUR:
        parts.push(this.translate('portTariffLines.preview.hours'));
        break;
      case PortTariffLineUnitTypes.METER:
      case PortTariffLineUnitTypes.TON:
        // For meter/ton the vessel property IS the primary multiplier — no separate unit label
        break;
      case PortTariffLineUnitTypes.CARGO:
        parts.push(this.translate('portTariffLines.preview.cargoUnits'));
        break;
      case PortTariffLineUnitTypes.CARGO_CBM:
        parts.push(this.translate('portTariffLines.preview.cargoCbm'));
        break;
      case PortTariffLineUnitTypes.CARGO_MT:
        parts.push(this.translate('portTariffLines.preview.cargoMt'));
        break;
      case PortTariffLineUnitTypes.CARGO_WM:
        parts.push(this.translate('portTariffLines.preview.cargoWm'));
        break;
      // FIXED: just the amount, no multiplier
    }

    if (hasProperty) {
      parts.push(this.vesselPropertyLabel(vesselProperty));
    }

    // Applied as the last factor on every standard line (including per-cargo-unit);
    // tax lines are forced to 1.0000 upstream and never reach here, so no exclusion needed.
    const quantityMultiplier = Number(line.quantityMultiplier ?? 1);
    if (quantityMultiplier !== 1) {
      parts.push(NumberUtils.formatRate(quantityMultiplier, 4));
    }

    return parts.join(' × ');
  }

  private translate(key: string, params?: Record<string, unknown>): string {
    return this.translateService.instant(key, params);
  }
}
